import React, { useState, useEffect } from "react";
import { Nav, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ChevronDown } from "react-bootstrap-icons";

export function NavItem({
  to,
  href,
  onClick,
  onRouteClick,
  external,
  label,
  icon: Icon,
  active,
  isActive,
  collapsed,
  badge,
  children,
}) {
  const hasChildren = Array.isArray(children) && children.length > 0;

  const hasActiveChild =
    hasChildren && isActive && children.some((c) => c.to && isActive(c.to));

  const [expanded, setExpanded] = useState(hasActiveChild);

  useEffect(() => {
    if (hasActiveChild) setExpanded(true);
  }, [hasActiveChild]);

  // ---- Expandable parent ----
  if (hasChildren && !collapsed) {
    return (
      <Nav.Item as="li">
        <button
          type="button"
          className={
            "cp-nav-item cp-nav-item--parent nav-link" +
            (expanded ? " cp-nav-item--expanded" : "")
          }
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <span className="cp-nav-item__icon">{Icon && <Icon />}</span>
          <span className="cp-nav-item__label">{label}</span>
          <ChevronDown
            className={
              "cp-nav-item__chevron" +
              (expanded ? " cp-nav-item__chevron--open" : "")
            }
            aria-hidden="true"
          />
        </button>

        {expanded && (
          <ul className="cp-nav-item__children">
            {children
              .filter((child) => !!child.to)
              .map((child) => (
                <li key={child.to}>
                  <Link
                    to={child.to}
                    onClick={() => onRouteClick?.(child.to)}
                    className={
                      "cp-nav-item cp-nav-item--child nav-link" +
                      (isActive && isActive(child.to)
                        ? " cp-nav-item--active"
                        : "")
                    }
                  >
                    <span
                      className="cp-nav-item__child-dot"
                      aria-hidden="true"
                    />
                    <span className="cp-nav-item__label">{child.label}</span>
                  </Link>
                </li>
              ))}
          </ul>
        )}
      </Nav.Item>
    );
  }

  // ---- Explicit Click Handlers & External Hrefs ----
  if (onClick || href) {
    return (
      <Nav.Item as="li">
        <a
          href={href || "#"}
          onClick={onClick}
          {...(external
            ? { target: "_blank", rel: "noopener noreferrer" }
            : {})}
          className="cp-nav-item nav-link"
          title={collapsed ? label : undefined}
        >
          <span className="cp-nav-item__icon">{Icon && <Icon />}</span>

          {!collapsed && (
            <>
              <span className="cp-nav-item__label">{label}</span>

              {badge != null && (
                <Badge bg="" className="cp-nav-item__badge">
                  {badge}
                </Badge>
              )}
            </>
          )}

          {collapsed && badge != null && (
            <span
              className="cp-nav-item__badge-dot"
              aria-label={`${badge} new`}
            />
          )}
        </a>
      </Nav.Item>
    );
  }

  // ---- Internal route — guard against undefined `to` ----
  if (!to) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        `[NavItem] Skipping render: no "to", "href", or "onClick" provided for label "${label}"`,
      );
    }
    return null;
  }

  return (
    <Nav.Item as="li">
      <Link
        to={to}
        onClick={() => onRouteClick?.(to)}
        className={
          "cp-nav-item nav-link" + (active ? " cp-nav-item--active" : "")
        }
        title={collapsed ? label : undefined}
      >
        <span className="cp-nav-item__icon">{Icon && <Icon />}</span>

        {!collapsed && (
          <>
            <span className="cp-nav-item__label">{label}</span>

            {badge != null && (
              <Badge bg="" className="cp-nav-item__badge">
                {badge}
              </Badge>
            )}
          </>
        )}

        {collapsed && badge != null && (
          <span
            className="cp-nav-item__badge-dot"
            aria-label={`${badge} new`}
          />
        )}
      </Link>
    </Nav.Item>
  );
}
